"use client";

import { ReactNode, useMemo } from "react";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { cn } from "@/lib/utils";

interface AnimatedSectionProps {
  children: ReactNode;
  animation?: "fade-up" | "fade-down" | "fade-left" | "fade-right" | "scale" | "blur" | "bounce-in";
  delay?: number;
  duration?: number;
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
  className?: string;
  enabled?: boolean;
}

/**
 * Wrapper component for scroll-triggered animations
 * 
 * @example
 * ```tsx
 * <AnimatedSection animation="fade-up" delay={100}>
 *   <div>Content that animates in</div>
 * </AnimatedSection>
 * ```
 */
export function AnimatedSection({
  children,
  animation = "fade-up",
  delay = 0,
  threshold = 0.1,
  rootMargin = "0px",
  once = true,
  className,
  enabled = true,
}: AnimatedSectionProps) {
  const { ref, isVisible } = useScrollReveal({
    threshold,
    rootMargin,
    once,
    enabled,
  });

  // Map animation type to initial and visible classes
  // Using standard Tailwind durations (500ms) for better performance
  const getAnimationClasses = useMemo(() => {
    const baseClasses = "transition-all duration-300 ease-out";
    
    if (!enabled || isVisible) {
      // Visible state
      switch (animation) {
        case "fade-up":
          return cn(baseClasses, "opacity-100 translate-y-0");
        case "fade-down":
          return cn(baseClasses, "opacity-100 translate-y-0");
        case "fade-left":
          return cn(baseClasses, "opacity-100 translate-x-0");
        case "fade-right":
          return cn(baseClasses, "opacity-100 translate-x-0");
        case "scale":
          return cn(baseClasses, "opacity-100 scale-100");
        case "blur":
          return cn(baseClasses, "opacity-100 blur-0");
        case "bounce-in":
          return cn(baseClasses, "opacity-100 scale-100");
        default:
          return cn(baseClasses, "opacity-100 translate-y-0");
      }
    } else {
      // Initial hidden state
      switch (animation) {
        case "fade-up":
          return cn(baseClasses, "opacity-0 translate-y-5");
        case "fade-down":
          return cn(baseClasses, "opacity-0 -translate-y-5");
        case "fade-left":
          return cn(baseClasses, "opacity-0 -translate-x-5");
        case "fade-right":
          return cn(baseClasses, "opacity-0 translate-x-5");
        case "scale":
          return cn(baseClasses, "opacity-0 scale-95");
        case "blur":
          return cn(baseClasses, "opacity-0 blur-sm");
        case "bounce-in":
          return cn(baseClasses, "opacity-0 scale-75");
        default:
          return cn(baseClasses, "opacity-0 translate-y-5");
      }
    }
  }, [animation, isVisible, enabled]);

  // For bounce-in, use animation class directly instead of transitions
  const shouldUseAnimation = animation === "bounce-in" && isVisible;
  const animationClass = shouldUseAnimation ? "animate-bounce-in" : "";

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn(
        animation !== "bounce-in" ? getAnimationClasses : (isVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"),
        animationClass,
        className
      )}
      style={{
        transitionDelay: animation !== "bounce-in" ? `${delay}ms` : undefined,
      }}
    >
      {children}
    </div>
  );
}
